import { useProfileContext } from "@hooks/context/useProfileContext";
import { useProjectContext } from "@hooks/context/useProjectContext";
import { useSidebarContext } from "@hooks/context/useSidebarContext";
import { useListProject } from "@hooks/crud/project/useListProject";
import { useSideBarInfo } from "@hooks/crud/sidebarMenu/useSideBarInfo";
import { crumbLabelFor } from "@layout/AppTopBar/AppTopBar.helpers";
import { SidebarLayoutType } from "@layout/MainSidebar/const";
import { MainSidebar } from "@layout/MainSidebar/MainSidebar";
import * as RouteHelper from "@utils/paths";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getNavMenuItems } from "./project-dashboard-layout-menu-items/project-dashboard-layout-menu-items";
import { shouldCloseSidebar } from "../shouldCloseSidebar";

/**
 * The full-screen flows: a report being written or read, a set-up wizard. The
 * nav folds on the way in because the page wants the width.
 *
 * Nothing else folds it — ordinary pages and switching between the
 * organisation and projects leave the nav as the user set it.
 */
const SIDEBAR_CLOSE_PATHS = [
  "/monthly-report-submission",
  "/package-setup",
  "/view-social-report",
  "/view-package-report",
  "/project-employee-setup"
];

interface Props {
  selectedItem: string;
  setSelectedItem: (item: string) => void;
}

const ProjectLayoutSidebarWrapper = ({
  selectedItem,
  setSelectedItem
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    contractorPackage,
    isHeadContractorPackage,
    isProjectLoading,
    selectedProject
  } = useProjectContext();

  const { isAuthProfileLoading, userOrganisation, userProfile } =
    useProfileContext();

  const { projects } = useListProject(userProfile, selectedProject?.archived);

  const { getSidebarMenuModel, sideBarMenuModel } = useSideBarInfo();
  const { isDrawOpen, setIsDrawOpen, setSectionLabel } = useSidebarContext();

  useEffect(() => {
    if (shouldCloseSidebar(location.pathname, SIDEBAR_CLOSE_PATHS)) {
      setIsDrawOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (contractorPackage?.id) {
      getSidebarMenuModel(contractorPackage.id);
    }
  }, [contractorPackage?.id]);

  const switchProject = useCallback(
    (projectId: string, contractorPackageId: string) =>
      navigate(RouteHelper.contractorDashboard(projectId, contractorPackageId)),
    [navigate]
  );

  const menuItems = useMemo(
    () =>
      getNavMenuItems(
        selectedProject,
        contractorPackage,
        userOrganisation,
        sideBarMenuModel,
        isHeadContractorPackage
      ),
    [
      selectedProject,
      contractorPackage,
      userOrganisation,
      sideBarMenuModel,
      isHeadContractorPackage
    ]
  );

  const loading = isAuthProfileLoading || isProjectLoading || !selectedProject;

  // The top bar's trailing crumb — see the organisation wrapper.
  useEffect(() => {
    setSectionLabel(crumbLabelFor(menuItems, selectedItem));
  }, [menuItems, selectedItem, setSectionLabel]);

  return (
    <MainSidebar
      isDrawOpen={isDrawOpen}
      loading={loading}
      menuItems={menuItems}
      selectedItem={selectedItem}
      setIsDrawOpen={setIsDrawOpen}
      setSelectedItem={setSelectedItem}
      layoutType={SidebarLayoutType.PROJECT}
      projects={projects}
      currentProject={selectedProject}
      currentContractorPackageId={contractorPackage?.id}
      switchProject={switchProject}
    />
  );
};

export default ProjectLayoutSidebarWrapper;
